import { ButtonType, ButtonWidget, CompactInlineAlertWidget, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, InlineAlertType, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { useSelector } from '~src/settings/general-settings/utils';

import { AuthFederationService, IAuthFederationAdmin } from './service/AuthFederationService';
import { WifConnectorsSection } from './WifConnectorsSection';
import { WifKeysSection } from './WifKeysSection';

export const AUTH_FEDERATION_SECTION_TEST_ID = 'auth-federation-section';
export const AUTH_FEDERATION_INITIAL_SETUP_BUTTON_TEST_ID = 'auth-federation-initial-setup-button';
export const AUTH_FEDERATION_DELETE_BUTTON_TEST_ID = 'auth-federation-delete-button';
export const AUTH_FEDERATION_DELETE_DIALOG_TEST_ID = 'auth-federation-delete-dialog';
export const AUTH_FEDERATION_DELETE_DIALOG_MESSAGE_TEST_ID = 'auth-federation-delete-dialog-message';
export const AUTH_FEDERATION_DELETE_DIALOG_CANCEL_TEST_ID = 'auth-federation-delete-dialog-cancel-button';
export const AUTH_FEDERATION_DELETE_DIALOG_PROCEED_TEST_ID = 'auth-federation-delete-dialog-proceed-button';
export const AUTH_FEDERATION_TOKEN_LIFETIME_TEST_ID = 'auth-federation-token-lifetime-input';
export const AUTH_FEDERATION_ISSUER_URL_TEST_ID = 'auth-federation-issuer-url';
export const AUTH_FEDERATION_COPY_ISSUER_TEST_ID = 'auth-federation-copy-issuer';
export const AUTH_FEDERATION_TOKEN_LIFETIME_SAVE_TEST_ID = 'auth-federation-token-lifetime-save';
export const AUTH_FEDERATION_TOKEN_LIFETIME_ERROR_TEST_ID = 'auth-federation-token-lifetime-error';

const MIN_TOKEN_LIFETIME_SECONDS = 60;

export function AuthFederationSection(): JSX.Element | null {
  const wifSiteEnabled = useSelector(SessionSiteSelectors.isWorkloadIdentityFederationEnabled);

  const [config, setConfig] = React.useState<IAuthFederationAdmin | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [settingUp, setSettingUp] = React.useState(false);
  const [setupError, setSetupError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);

  const [tokenLifetimeInput, setTokenLifetimeInput] = React.useState('');
  const [tokenLifetimeError, setTokenLifetimeError] = React.useState<string | null>(null);
  const [savingTokenLifetime, setSavingTokenLifetime] = React.useState(false);

  const [copySuccess, setCopySuccess] = React.useState(false);

  React.useEffect(() => {
    if (!wifSiteEnabled) {
      setLoading(false);
      return;
    }
    AuthFederationService.getAdmin()
      .then((admin) => {
        setConfig(admin);
        setTokenLifetimeInput(String(admin.tokenLifetimeSeconds));
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [wifSiteEnabled]);

  const handleInitialSetup = () => {
    setSetupError(null);
    setSettingUp(true);

    AuthFederationService.initialEnable()
      .then((admin) => {
        setConfig(admin);
        setTokenLifetimeInput(String(admin.tokenLifetimeSeconds));
        getSessionManagementServiceInstance().refreshSessionInfo();
      })
      .catch(() => setSetupError(Messages.authFederationEnableError()))
      .finally(() => setSettingUp(false));
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteError(null);
    setDeleting(true);
    setShowDeleteDialog(false);

    AuthFederationService.deleteAdmin()
      .then(() => {
        setConfig(null);
        setTokenLifetimeInput('');
        getSessionManagementServiceInstance().refreshSessionInfo();
      })
      .catch(() => setDeleteError(Messages.authFederationDeleteError()))
      .finally(() => setDeleting(false));
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };

  const handleTokenLifetimeChange = (value: string) => {
    setTokenLifetimeInput(value);
    setTokenLifetimeError(null);
  };

  const handleTokenLifetimeSave = () => {
    const parsed = parseInt(tokenLifetimeInput, 10);
    if (isNaN(parsed) || !Number.isInteger(parsed) || parsed < MIN_TOKEN_LIFETIME_SECONDS) {
      setTokenLifetimeError(Messages.authFederationTokenLifetimeError({ MIN_SECONDS: String(MIN_TOKEN_LIFETIME_SECONDS) }));
      return;
    }
    setTokenLifetimeError(null);
    setSavingTokenLifetime(true);
    AuthFederationService.patch({ tokenLifetimeSeconds: parsed })
      .then((admin) => {
        setConfig(admin);
        setTokenLifetimeInput(String(admin.tokenLifetimeSeconds));
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.authFederationTokenLifetimeSaveSuccess() });
      })
      .catch(() => {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.authFederationTokenLifetimeSaveError() });
      })
      .finally(() => setSavingTokenLifetime(false));
  };

  const handleCopyIssuer = () => {
    if (!config?.issuerUrl) return;
    const doCopy = () => {
      const textarea = document.createElement('textarea');
      textarea.value = config.issuerUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    };
    const onSuccess = () => { setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); };
    const onFailure = () => { ToasterService.instance.pop({ type: ToastType.Error, text: Messages.authFederationCopyIssuerError() }); };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(config.issuerUrl)
        .then(onSuccess)
        .catch(() => { if (doCopy()) { onSuccess(); } else { onFailure(); } });
    } else if (doCopy()) {
      onSuccess();
    } else {
      onFailure();
    }
  };

  if (!wifSiteEnabled) {
    return null;
  }

  if (loading) {
    return (
      <SettingsSection title={Messages.authFederationTitle()} testId={AUTH_FEDERATION_SECTION_TEST_ID}>
        <div data-tb-test-id='auth-federation-loading'>{Messages.authFederationLoading()}</div>
      </SettingsSection>
    );
  }

  if (loadError) {
    return (
      <SettingsSection title={Messages.authFederationTitle()} testId={AUTH_FEDERATION_SECTION_TEST_ID}>
        <CompactInlineAlertWidget type={InlineAlertType.Error} testId='auth-federation-load-error'>
          <div>{Messages.authFederationLoadError()}</div>
        </CompactInlineAlertWidget>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      title={Messages.authFederationTitle()}
      description={Messages.authFederationDescription()}
      testId={AUTH_FEDERATION_SECTION_TEST_ID}>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {!config?.enabled ? (
          <ButtonWidget
            buttonType={ButtonType.Primary}
            testId={AUTH_FEDERATION_INITIAL_SETUP_BUTTON_TEST_ID}
            type='button'
            disabled={settingUp}
            onClick={handleInitialSetup}
          >
            {Messages.authFederationInitialSetupButton()}
          </ButtonWidget>
        ) : (
          <ButtonWidget
            buttonType={ButtonType.Destructive}
            testId={AUTH_FEDERATION_DELETE_BUTTON_TEST_ID}
            type='button'
            disabled={deleting}
            onClick={handleDeleteClick}
          >
            {Messages.authFederationDeleteButton()}
          </ButtonWidget>
        )}
      </div>

      {setupError && (
        <CompactInlineAlertWidget type={InlineAlertType.Error} testId='auth-federation-setup-error'>
          <div>{setupError}</div>
        </CompactInlineAlertWidget>
      )}

      {deleteError && (
        <CompactInlineAlertWidget type={InlineAlertType.Error} testId='auth-federation-delete-error'>
          <div>{deleteError}</div>
        </CompactInlineAlertWidget>
      )}

      {showDeleteDialog && (
        <DialogWidget
          title={Messages.authFederationDeleteDialogTitle()}
          testId={AUTH_FEDERATION_DELETE_DIALOG_TEST_ID}
          type={DialogType.NormalWithTitleBar}
          onCloseRequest={handleDeleteCancel}
          showCloseButton={true}
          positioningBehavior={DialogPositioningBehavior.Centered}
          isModal={true}
          showFooter={true}
          handleGlassClick={handleDeleteCancel}
          isGlassTransparent={false}
          footerContent={
            <DialogFooterButtonsWidget
              buttons={[
                {
                  handleClick: handleDeleteCancel,
                  buttonType: ButtonType.Outline,
                  children: Messages.authFederationDeleteDialogCancel(),
                  testId: AUTH_FEDERATION_DELETE_DIALOG_CANCEL_TEST_ID
                },
                {
                  handleClick: handleDeleteConfirm,
                  buttonType: ButtonType.Destructive,
                  children: Messages.authFederationDeleteDialogConfirm(),
                  testId: AUTH_FEDERATION_DELETE_DIALOG_PROCEED_TEST_ID
                }
              ]}
            />
          }
        >
          <div data-tb-test-id={AUTH_FEDERATION_DELETE_DIALOG_MESSAGE_TEST_ID}>
            {Messages.authFederationDeleteDialogMessage()}
          </div>
        </DialogWidget>
      )}

      {config?.enabled && (
        <div>
          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 'bold', flexShrink: 0 }}>{Messages.authFederationIssuerUrlLabel()}</span>
            <span
              data-tb-test-id={AUTH_FEDERATION_ISSUER_URL_TEST_ID}
              style={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}
            >
              {config.issuerUrl}
            </span>
            <ButtonWidget
              buttonType={ButtonType.Outline}
              testId={AUTH_FEDERATION_COPY_ISSUER_TEST_ID}
              type='button'
              onClick={handleCopyIssuer}
            >
              {copySuccess ? Messages.authFederationCopyIssuerCopied() : Messages.authFederationCopyIssuerButton()}
            </ButtonWidget>
          </div>

          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <TextFieldWidget
                label={Messages.authFederationTokenLifetimeLabel()}
                testId={AUTH_FEDERATION_TOKEN_LIFETIME_TEST_ID}
                value={tokenLifetimeInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTokenLifetimeChange(e.target.value)}
              />
              <ButtonWidget
                buttonType={ButtonType.Primary}
                testId={AUTH_FEDERATION_TOKEN_LIFETIME_SAVE_TEST_ID}
                type='button'
                disabled={savingTokenLifetime}
                onClick={handleTokenLifetimeSave}
              >
                {Messages.authFederationSaveButton()}
              </ButtonWidget>
            </div>
            {tokenLifetimeError && (
              <CompactInlineAlertWidget type={InlineAlertType.Error} testId={AUTH_FEDERATION_TOKEN_LIFETIME_ERROR_TEST_ID}>
                <div>{tokenLifetimeError}</div>
              </CompactInlineAlertWidget>
            )}
          </div>

          <WifConnectorsSection
            issuerUrl={config.issuerUrl}
            tokenLifetimeSeconds={config.tokenLifetimeSeconds}
          />

          <WifKeysSection
            activeKeyId={config.activeKeyId}
            onActiveKeyIdChange={(newId) => setConfig(prev => prev ? { ...prev, activeKeyId: newId } : prev)}
          />
        </div>
      )}
    </SettingsSection>
  );
}
